/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/manager/ManagerApprovalQueue.tsx
 * PURPOSE: Manager Approval Queue page — Sprint 75 S75-004/S75-005
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-03-20 → 2026-08-09 (đồng bộ giao diện với CSS Module, gỡ inline style)
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P2 (Sprint Governance) + P4 (Quality Gates) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 7: Performance SLAs (<50ms p95)
 * SPRINT: S75-004 (ManagerApprovalQueue component) + S75-005 (route /dashboard/manager/approvals)
 *
 * DoD:
 *   - Vietnamese labels: "Chờ duyệt", "Phê duyệt", "Từ chối" ✅
 *   - Audit trail: bulk action logged by backend ✅
 *   - Real data from GET /api/v2/manager/pending-approvals ✅
 *   - POST /api/v2/manager/bulk-approve with reason (reject) ✅
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager Approval Workflow
 * - Impact: Revenue Critical — shift swap + leave request approval queue
 * - Stakeholders: Branch Managers, HR Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer (Page Component)
 * - Pattern: TanStack Query v5, useBulkApprove mutation, optimistic UI
 * - Dependencies: usePendingApprovals, useBulkApprove, react-router-dom
 *
 * @cross-references
 * - Hooks: frontend/src/hooks/useManagerDashboard.ts
 * - API: frontend/src/services/api/managerApi.ts
 * - Backend: backend/app/api/v2/endpoints/manager_approvals.py
 * - Route: frontend/src/App.tsx (/dashboard/manager/approvals)
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0407-manager-dashboards-design.md
 * - Style: frontend/src/pages/manager/ManagerApprovalQueue.module.css — BUG THẬT
 *   đã sửa (2026-08-09): màn này trước đây là màn Manager DUY NHẤT không dùng
 *   CSS Module như các màn khác (ManagerAttendancePage/ManagerSchedulePage) —
 *   toàn bộ style là style={{...}} tay từng phần tử, font-size/spacing/màu
 *   lệch khỏi phần còn lại của web Manager. Chuyển sang cùng CSS Module
 *   convention (--nqh-* design tokens), KHÔNG đổi logic/hành vi component.
 * ===============================================================================
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { usePendingApprovals, useBulkApprove } from '../../hooks/useManagerDashboard';
import { managerApi } from '../../services/api/managerApi';
import type { PendingApprovalItem, LeaveHistoryItem } from '../../services/api/managerApi';
import { DateRangePicker, type DateRangeValue } from '../../components/ui/DateRangePicker';
import styles from './ManagerApprovalQueue.module.css';
import { toLocalISODate } from '@/utils/date';

// =============================================================================
// Icons — inline SVG (Heroicons outline, path data khớp @heroicons/react —
// cùng pattern "Inline SVG icons... 0 emoji" đã codify ở ManagerStaffPage.tsx
// và các trang Manager khác). ISS-MGR-13 (2026-08-10, Hang Le): đổi toàn bộ
// emoji Unicode trên màn Duyệt đơn sang SVG — KHÔNG dùng emoji.
// =============================================================================

const SwapIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);

const CalendarIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
  </svg>
);

const CheckCircleIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75l2.25 2.25L15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Path giống hệt WarnIcon ở ManagerHubPage.tsx / ManagerSchedulePage.tsx / ManagerProfilePage.tsx
const WarningIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);

// Path giống hệt CheckIcon ở ManagerSchedulePage.tsx
const CheckIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

// Path giống hệt CloseIcon ở ManagerStaffPage.tsx / ManagerAttendancePage.tsx / ManagerSchedulePage.tsx / ManagerHubPage.tsx
const XMarkIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={size} height={size} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// =============================================================================
// Helpers
// =============================================================================

const PRIORITY_CLASS: Record<string, string> = {
  high: styles.priorityHigh,
  medium: styles.priorityMedium,
  low: styles.priorityLow,
};

const PRIORITY_VI: Record<string, string> = {
  high: 'Cao',
  medium: 'TB',
  low: 'Thấp',
};

const TYPE_ICON: Record<string, React.FC<{ size?: number }>> = {
  shift_swap:     SwapIcon,
  leave_request:  CalendarIcon,
};

// Chip lọc loại (Tất cả/Xin nghỉ) đã BỎ 2026-08-30 — gộp vào 3-nút 1 hàng
// (Tất cả/Chờ duyệt/Lịch sử nghỉ phép) ở đầu trang. `approval_type` truyền
// cho usePendingApprovals() giờ luôn `undefined` (không lọc theo loại nữa) —
// shift_swap vẫn không có dữ liệu thật vì hệ ShiftSwap-model này khác với
// Variant B mobile "Đổi ca" mới (DD-0106, /api/v2/schedule/swap/*), nên
// TYPE_ICON/TYPE_VI/getSelectedByType xử lý shift_swap vẫn GIỮ NGUYÊN,
// chỉ chờ dữ liệu, không phải code chết.
const TYPE_VI: Record<string, string> = {
  shift_swap: 'Đổi ca',
  leave_request: 'Xin nghỉ',
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('vi-VN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// =============================================================================
// Component
// =============================================================================

const ManagerApprovalQueue: React.FC = () => {
  // 2026-08-30: gộp 3 nút "Tất cả" / "Chờ duyệt" / "Lịch sử nghỉ phép" vào
  // ĐÚNG 1 hàng (trước đây 2 hàng: tab Chờ duyệt|Lịch sử + chip lọc Tất cả|
  // Xin nghỉ bên trong "Chờ duyệt" — chip "Xin nghỉ" đã gộp thẳng vào "Chờ
  // duyệt" theo yêu cầu, không còn chip lọc riêng nữa).
  // - "all": xem HẾT mọi đơn nghỉ phép (mọi trạng thái), KHÔNG lọc ngày.
  // - "pending": hàng đợi chờ duyệt (giữ nguyên hành vi cũ).
  // - "history": lịch sử CÓ bộ lọc khoảng ngày (Date & Time picker) — tiền đề
  //   tổng quan "Phiếu Lương".
  const [viewMode, setViewMode] = useState<'all' | 'pending' | 'history'>('pending');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [pendingRejectAction, setPendingRejectAction] = useState<{
    ids: string[];
    type: 'shift_swap' | 'leave_request';
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = usePendingApprovals({
    approval_type: undefined,
    page_size: 50,
  });

  const bulkApproveMutation = useBulkApprove();

  const items = data?.items ?? [];

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.id)));
    }
  };

  const getSelectedByType = (type: 'shift_swap' | 'leave_request') =>
    items
      .filter(i => selectedIds.has(i.id) && i.approval_type === type)
      .map(i => i.id);

  const handleBulkApprove = async (action: 'approve' | 'reject') => {
    const swapIds = getSelectedByType('shift_swap');
    const leaveIds = getSelectedByType('leave_request');

    if (swapIds.length === 0 && leaveIds.length === 0) return;

    if (action === 'reject') {
      // Collect which types we're rejecting
      const firstType = swapIds.length > 0 ? 'shift_swap' : 'leave_request';
      setPendingRejectAction({ ids: swapIds.length > 0 ? swapIds : leaveIds, type: firstType });
      setShowRejectModal(true);
      return;
    }

    // Approve — process each type
    const promises = [];
    if (swapIds.length > 0) {
      promises.push(bulkApproveMutation.mutateAsync({
        item_ids: swapIds, approval_type: 'shift_swap', action: 'approve',
      }));
    }
    if (leaveIds.length > 0) {
      promises.push(bulkApproveMutation.mutateAsync({
        item_ids: leaveIds, approval_type: 'leave_request', action: 'approve',
      }));
    }

    await Promise.all(promises);
    setSelectedIds(new Set());
    setSuccessMessage(`Đã phê duyệt ${selectedIds.size} yêu cầu`);
    setTimeout(() => setSuccessMessage(null), 3000);
    refetch();
  };

  const handleConfirmReject = async () => {
    if (!pendingRejectAction || !rejectReason.trim()) return;

    await bulkApproveMutation.mutateAsync({
      item_ids: pendingRejectAction.ids,
      approval_type: pendingRejectAction.type,
      action: 'reject',
      reason: rejectReason.trim(),
    });

    setShowRejectModal(false);
    setRejectReason('');
    setPendingRejectAction(null);
    setSelectedIds(new Set());
    setSuccessMessage(`Đã từ chối ${pendingRejectAction.ids.length} yêu cầu`);
    setTimeout(() => setSuccessMessage(null), 3000);
    refetch();
  };

  return (
    <div>
      {/* Trang này giờ nằm TRONG <ManagerLayout> (sidebar + topbar đã hiện
          tên tổ chức + "Duyệt đơn nghỉ phép" — xem ManagerLayout.tsx
          activeItem.label). Header/back-button/nền full-page riêng của bản
          Sprint 75 gốc đã bị GỠ — nó tự vẽ lại một trang đứng riêng, và nút
          "← Quay lại" trỏ cứng sang /dashboard/manager (dashboard CŨ, khác
          hẳn shell mới), khiến người dùng bị bật ra khỏi UI mới khi bấm. */}

      {/* 1 HÀNG DUY NHẤT: "Tất cả" / "Chờ duyệt" / "Lịch sử nghỉ phép"
          (2026-08-30, theo phản hồi user — trước đây 2 hàng: tab Chờ duyệt|
          Lịch sử ở trên + chip lọc Tất cả|Xin nghỉ tách riêng bên dưới, gộp
          "Chờ duyệt" + "Xin nghỉ" cũ thành 1 nút "Chờ duyệt" duy nhất). */}
      <div className={styles.filterChips} style={{ marginBottom: 'var(--nqh-space-4)' }}>
        <button
          type="button"
          onClick={() => { setViewMode('all'); setSelectedIds(new Set()); }}
          className={`${styles.filterChip} ${viewMode === 'all' ? styles.filterChipActive : ''}`}
        >
          Tất cả
        </button>
        <button
          type="button"
          onClick={() => { setViewMode('pending'); setSelectedIds(new Set()); }}
          className={`${styles.filterChip} ${viewMode === 'pending' ? styles.filterChipActive : ''}`}
        >
          Chờ duyệt
        </button>
        <button
          type="button"
          onClick={() => { setViewMode('history'); setSelectedIds(new Set()); }}
          className={`${styles.filterChip} ${viewMode === 'history' ? styles.filterChipActive : ''}`}
        >
          <CalendarIcon size={14} /> Lịch sử nghỉ phép
        </button>
      </div>

      {viewMode === 'all' ? (
        <LeaveHistorySection showDateFilter={false} />
      ) : viewMode === 'history' ? (
        <LeaveHistorySection showDateFilter />
      ) : (
        <>
      {/* Tổng quan số lượng — thay cho header cũ */}
      {data && (
        <div className={styles.pageHead}>
          <span className={styles.pageHeadCount}>{data.total_count} yêu cầu đang chờ</span>
          {/* Badge "X xin nghỉ" đã bỏ theo yêu cầu 2026-08-17 — trùng ý với
              số tổng bên trái (hiện tại chỉ có loại "Xin nghỉ", số lúc nào
              cũng khớp total_count, không thêm thông tin gì). Chip lọc loại
              (Tất cả/Xin nghỉ) đã BỎ HẲN 2026-08-30 — gộp vào 3-nút 1 hàng
              phía trên, "Chờ duyệt" giờ luôn hiện mọi loại (chỉ leave_request
              thật sự có dữ liệu, shift_swap vẫn ẩn theo lý do cũ). */}
        </div>
      )}

      <div>
        {/* Success message */}
        {successMessage && (
          <div className={`${styles.banner} ${styles.bannerSuccess}`} role="status">
            <CheckCircleIcon size={18} /> {successMessage}
          </div>
        )}

        {/* Error display */}
        {bulkApproveMutation.isError && (
          <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
            <WarningIcon size={18} /> {(bulkApproveMutation.error as { message?: string } | null)?.message || "Lỗi xử lý: Vui lòng thử lại"}
          </div>
        )}

        {/* Bulk Actions Bar — chip lọc loại (Tất cả/Xin nghỉ) đã BỎ 2026-08-30,
            gộp vào 3-nút 1 hàng phía trên trang (xem comment ở return chính). */}
        <div className={styles.filterBar}>
          {/* Bulk actions — only shown when items selected */}
          {selectedIds.size > 0 && (
            <div className={styles.bulkActions}>
              <span className={styles.bulkActionsLabel}>
                Đã chọn {selectedIds.size}
              </span>
              <button
                type="button"
                onClick={() => handleBulkApprove('approve')}
                disabled={bulkApproveMutation.isPending}
                className={`${styles.btn} ${styles.btnSuccess}`}
              >
                <CheckIcon size={14} /> Phê duyệt
              </button>
              <button
                type="button"
                onClick={() => handleBulkApprove('reject')}
                disabled={bulkApproveMutation.isPending}
                className={`${styles.btn} ${styles.btnDanger}`}
              >
                <XMarkIcon size={14} /> Từ chối
              </button>
            </div>
          )}
        </div>

        {/* Items List */}
        <div className={styles.listWrap}>
          {/* Table header */}
          <div className={styles.listHead}>
            <div className={styles.checkboxCell}>
              <input
                type="checkbox"
                checked={selectedIds.size === items.length && items.length > 0}
                onChange={toggleSelectAll}
                className={styles.checkbox}
              />
            </div>
            {['Người yêu cầu', 'Loại', 'Mô tả', 'Thời gian', 'Độ ưu tiên'].map(h => (
              <span key={h} className={styles.listHeadCell}>
                {h}
              </span>
            ))}
          </div>

          {/* Rows */}
          {isLoading ? (
            <div className={styles.loadingState}>
              Đang tải...
            </div>
          ) : error ? (
            <div className={styles.errorState}>
              <WarningIcon size={16} /> Lỗi tải dữ liệu. <button type="button" onClick={() => refetch()}>Thử lại</button>
            </div>
          ) : items.length === 0 ? (
            <div className={styles.emptyState}>
              {/* Theo yêu cầu 2026-08-17: bỏ icon dấu tick trang trí — chỉ
                  còn chữ, không mất chức năng gì (icon không có onClick). */}
              <div className={styles.emptyTitle}>
                Không có yêu cầu nào đang chờ
              </div>
              <div className={styles.emptySubtitle}>
                Tất cả yêu cầu đã được xử lý
              </div>
            </div>
          ) : (
            items.map((item: PendingApprovalItem) => {
              const TypeIcon = TYPE_ICON[item.approval_type];
              return (
              <div
                key={item.id}
                className={`${styles.row} ${selectedIds.has(item.id) ? styles.rowSelected : ''}`}
                onClick={() => toggleSelect(item.id)}
              >
                {/* Checkbox */}
                <div className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    onClick={e => e.stopPropagation()}
                    className={styles.checkbox}
                  />
                </div>

                {/* Requester */}
                <div>
                  <div className={styles.requesterName}>
                    {item.requester_name}
                  </div>
                  <div className={styles.requesterId}>
                    ID: {item.requester_id.slice(0, 8)}…
                  </div>
                </div>

                {/* Type */}
                <span
                  className={`${styles.typeChip} ${item.approval_type === 'shift_swap' ? styles.typeChipSwap : ''}`}
                >
                  <TypeIcon size={13} /> {TYPE_VI[item.approval_type]}
                </span>

                {/* Description */}
                <div className={styles.description}>
                  {item.description}
                </div>

                {/* Requested at */}
                <div className={styles.requestedAt}>
                  {formatDateTime(item.requested_at)}
                </div>

                {/* Priority */}
                <span className={`${styles.priorityChip} ${PRIORITY_CLASS[item.priority] ?? ''}`}>
                  {PRIORITY_VI[item.priority]}
                </span>
              </div>
              );
            })
          )}
        </div>
      </div>
        </>
      )}

      {/* ====== REJECT MODAL ====== */}
      {showRejectModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="rejectModalTitle">
            <h3 className={styles.modalTitle} id="rejectModalTitle">
              Từ chối yêu cầu
            </h3>
            <p className={styles.modalBody}>
              Bạn đang từ chối {pendingRejectAction?.ids.length ?? 0} yêu cầu.
              Vui lòng nhập lý do từ chối.
            </p>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Lý do từ chối... (bắt buộc)"
              className={styles.modalTextarea}
            />
            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={() => { setShowRejectModal(false); setRejectReason(''); setPendingRejectAction(null); }}
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                Hủy
              </button>
              <button
                type="button"
                onClick={handleConfirmReject}
                disabled={!rejectReason.trim() || bulkApproveMutation.isPending}
                className={`${styles.btn} ${styles.btnDanger}`}
              >
                {bulkApproveMutation.isPending ? 'Đang xử lý...' : 'Xác nhận từ chối'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// "Lịch sử nhân viên nghỉ phép" — tab thứ 2, tiền đề tổng quan "Phiếu Lương"
// (2026-08-30). Khác Chờ duyệt: hiện MỌI trạng thái, lọc theo khoảng ngày
// qua DateRangePicker (dùng chung component với popover "Đổi ca" lưới phân ca).
// =============================================================================

const LEAVE_TYPE_VI_HISTORY: Record<string, string> = {
  annual: "Nghỉ phép năm",
  sick: "Nghỉ ốm",
  maternity: "Nghỉ thai sản",
  personal: "Nghỉ việc riêng",
  unpaid: "Nghỉ không lương",
  public_holiday: "Nghỉ lễ",
  wedding: "Nghỉ kết hôn",
  funeral: "Nghỉ tang gia",
  child_wedding: "Nghỉ con kết hôn",
};

const LEAVE_STATUS_VI_HISTORY: Record<string, { label: string; className: string }> = {
  approved: { label: "Đã duyệt", className: "" },
  rejected: { label: "Đã từ chối", className: "" },
  cancelled: { label: "Đã huỷ", className: "" },
  pending: { label: "Chờ duyệt", className: "" },
  submitted: { label: "Chờ duyệt", className: "" },
  manager_review: { label: "Chờ duyệt", className: "" },
};

function formatDateVi(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

interface LeaveHistorySectionProps {
  /** false = tab "Tất cả" — xem HẾT, không hiện DateRangePicker, không lọc ngày. */
  showDateFilter: boolean;
}

const LeaveHistorySection: React.FC<LeaveHistorySectionProps> = ({ showDateFilter }) => {
  // Mặc định 30 ngày gần nhất — đủ dùng ngay, vẫn đổi được qua DateRangePicker.
  const today = new Date();
  const thirtyDaysAgo = new Date(today.getTime() - 30 * 86400000);
  const toISO = toLocalISODate;

  const [range, setRange] = useState<DateRangeValue>({
    start: toISO(thirtyDaysAgo),
    end: toISO(today),
  });

  // Tab "Tất cả" (showDateFilter=false): không gửi start_date/end_date —
  // backend trả HẾT mọi đơn nghỉ phép, mọi trạng thái, không giới hạn ngày.
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: showDateFilter ? ["manager", "leave-history", range.start, range.end] : ["manager", "leave-history", "all"],
    queryFn: () =>
      managerApi.getLeaveHistory(
        showDateFilter
          ? { start_date: range.start ?? undefined, end_date: range.end ?? undefined, page_size: 100 }
          : { page_size: 100 },
      ),
    enabled: !showDateFilter || Boolean(range.start && range.end),
  });

  const items: LeaveHistoryItem[] = data?.items ?? [];

  return (
    <div>
      <div className={styles.filterBar}>
        {showDateFilter ? (
          <DateRangePicker value={range} onChange={setRange} maxSpanDays={366} />
        ) : null}
        <span className={styles.pageHeadCount}>
          {data ? `${data.total_count} đơn` : ""}
        </span>
      </div>

      {isLoading && <div className={styles.loadingState}>Đang tải…</div>}

      {error && (
        <div className={styles.errorState}>
          Lỗi tải lịch sử nghỉ phép. <button onClick={() => refetch()}>Thử lại</button>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📅</div>
          <div className={styles.emptyTitle}>
            {showDateFilter ? "Không có đơn nghỉ phép nào trong khoảng ngày đã chọn" : "Chưa có đơn nghỉ phép nào"}
          </div>
        </div>
      )}

      <div className={styles.listWrap}>
        {items.map((item) => {
          const statusInfo = LEAVE_STATUS_VI_HISTORY[item.status] ?? { label: item.status, className: "" };
          return (
            <div
              key={item.id}
              className={styles.row}
              style={{
                // BUG THẬT đã sửa (2026-08-31, user báo "lỗi font chữ" — thực
                // ra là chữ vỡ dòng đè lên nhau, y hệt lỗi đã vá ở
                // ShiftSwapReviewPage.tsx cùng ngày): `.row` là CSS Grid 6 cột
                // hẹp (40px/1fr/…) — `flexDirection` không có tác dụng trên
                // `display: grid`, khiến tên NV bị nhồi vào cột 40px và vỡ
                // chữ từng dòng đè lên khối mô tả bên cạnh.
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "var(--nqh-space-2)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--nqh-space-2)" }}>
                <span className={styles.requesterName}>{item.employee_name}</span>
                <span className={styles.typeChip}>{LEAVE_TYPE_VI_HISTORY[item.leave_type] ?? item.leave_type}</span>
                <span className={styles.priorityChip}>{statusInfo.label}</span>
              </div>
              <div className={styles.description}>
                {formatDateVi(item.start_date)} → {formatDateVi(item.end_date)}
                {item.working_days != null ? ` · ${item.working_days} ngày công` : ""}
              </div>
              {item.reason ? <div className={styles.description}>Lý do: {item.reason}</div> : null}
              {item.status === "rejected" && item.rejection_reason ? (
                <div className={styles.description}>Lý do từ chối: {item.rejection_reason}</div>
              ) : null}
              {item.reviewed_by_name ? (
                <div className={styles.requestedAt}>Xử lý bởi: {item.reviewed_by_name}</div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ManagerApprovalQueue;
